import { Page } from '../../types'
import { COUNTRIES } from '../../defaultContent'

const addDetectedFraud: Page[] = [
    {
        pageType: 'information',
        title: 'Before you start',
        name: 'deducted-fraud-before-you-start',
        description:
            'This typology refers to the amount of fraud detected within the given quarter. Detected fraud includes instances of identified fraud, where, post-payment, it was decided that on the balance of probabilities the intention was to defraud. For reporting purposes, this should not include instances of fraud that were prevented from payment after the point of detection (these should be reported in Section 2). Entries should record the number of instances and the value of the loss relating to the fraud. Entries must include selections from the following typology dropdowns:<ul><li>the business area affected by the fraud</li><li>who committed the fraud?</li><li>the nature of deception - whether the prevented fraud involved a false representation, a failure to declare or disclose, or an abuse of position</li><li>the detailed nature of deception - a dependent dropdown selection on the specifics of the fraudulent activity</li><li>if applicable, the fraud enabler(s)</li><li>whether or not the fraud related to a COVID-19 scheme or activity</li></ul><br/>',
    },
    {
        pageType: 'question',
        title: 'Detected fraud',
        name: 'detected-fraud',
        description:
            'This section refers to all instances of fraud, error or bribery that were detected (after the event) in the given quarter. Instances should be reported in the CDR in the quarter it was determined to be fraud, error or bribery. For example, where a fraud occurred in quarter 2 but was undetected until quarter 4, this would be included in the Q4 return. All instances of detected fraud, error or bribery should refer to actual instances and values, not estimates.\n'+
            'Detected Fraud should be reported at the point at which it has been determined fraud, on the civil balance of probabilities, regardless of whether prosecution to the criminal standard is being progressed. This can include instances where fraud is proven to both the civil and criminal standards simultaneously.\n'+
            'Please note: All entries in this section must be attributed to a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail',
        questions: [
            {
                questionType: 'radio',
                title: 'Do you have any instances of detected fraud to declare?',
                name: 'detected-fraud-declare',
                options: ['Yes', 'No'],
                rules: {
                    required: true
                }
            }
        ]
    },
    {
        pageType: 'list',
        title: 'Add an instance',
        name: 'deducted-fraud-add-instance',
        description:
            'Detected Fraud should be reported at the point at which it has been determined fraud, on the civil balance of probabilities, regardless of whether prosecution to the criminal standard is being progressed. This can include instances where fraud is proven to both the civil and criminal standards simultaneously.<br/><br/>Please note: All entries in this section must be attributed to a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail',
        disabled: [
            {
                field: 'detected-fraud-declare',
                condition: 'equals', // optional
                value: 'No'
            }
        ],
        questions: [
            {
                questionType: 'select',
                title: 'Which spending area did the fraud occur in?',
                name: 'detected-fraud-area',
                detailsHeading: 'Help with spending areas',
                detailsContent: ' <b>Procurement:</b> Goods or services bought by public sector organisations (including suppliers)' +
                                ' <br/><b>Personnel:</b> Employees (including contractors and agency workers).' +
                                ' <br/><b>Subsidies:</b> Support or incentives given by the government.' +
                                ' <br/><b>Grants:</b> Money given by a public sector organisation for a specific purpose.' +
                                ' <br/><b>Loans & credit guarantees:</b> Money given by a public sector organisation for a specific purpose.' +
                                ' <br/><b>Charges & other non-tax income:</b> Money owed to public sector organisations (for example, court fines or prescription fees)' +
                                ' <br/><b>Licenses and permits:</b> Personal or business licenses (for example, a Blue Badge or Surveyor license)' +
                                ' <br/><b>Tax & Duties:</b> Mandatory government charge for goods, income or services' +
                                ' <br/><b>Benefits:</b> Money given by the government to support members of the public' +
                                ' <br/><b>Special Payments:</b> Money given in unique or specific circumstances (for example, compensation)',
                options: ['Procurement','Personnel','Subsidies','Grants','Loans & credit gurantees','Charges & other non-tax income','Licences and permits','Tax & Duties','Benefits','Special payments'],
                hintOption: 'Select an option',
                rules: {
                    required: true
                },
                errorMessages: { required: 'This field is mandatory' }
            },    
            {
                questionType: 'select',
                title: 'Who was responsible for the fraud?',
                name: 'deducted-fraud--who-is-responsible',
                detailsHeading: 'Help with responsibility',
                detailsContent: '<b>Third sector organisations:</b> a non-governmental organisation (for example, charities, social enterprises, and voluntary groups)' +
                ' <br/><b>Private sector - company:</b> a business owned by shareholders but run by its directors' +
                ' <br/><b>Private sector - partnerships:</b> a business owned and run by its partners' +
                ' <br/><b>Private sector - self employed:</b> in their role as a self-employed person (for example, a sole trader)' +
                ' <br/><b>Public sector organisations:</b> (for example, NHS Trusts, higher education providers and academies)' +
                ' <br/><b>Members of the general public:</b> in their role as member of the public' +
                ' <br/><b>Hostile third party:</b> (for example, foreign states, "hacktivist" groups and organised crime groups)'+
                ' <br/><b>Public sector employees:</b> in their role as a person employed by the government (for example, a public servant or health care employee)' +
                ' <br/><b>Contingent labour:</b> in their role as a person contracted to supply a service or contingent labour to government' +
                ' <br/><b>Other public servants:</b> in their role as person supporting the government without pay (for example, jurors, volunteers or magistrates)' +
                ' <br/><b>Unknown:</b> I dont know who was responsible for the fraud',
                options: ['Third sector organisations','Private sector - company','Private sector - partnerships','Private sector - self employed','Public sector organisations','Members of the graneral public','Hostile third party','Public sector employees','Contigent labour','Other public servants','Unknown'],
                hintOption: 'Select an option',
                rules: {
                    required: true
                },
                errorMessages: { required: 'This field is mandatory' }
            },        
            {
                name: 'deducted-fraud-deception',
                questionType: 'radio',
                title: 'What was the general nature of the deception?',
                detailsHeading: 'Help with general nature of deception',
                detailsContent: ' <b>Abuse of position:</b> use of a persons position or role in the government or public sector'+
                                ' <br/><b>Failure to declare/disclose:</b> Intentionally omitting information you legally need to share' +                
                                ' <br/><b>False Representation:</b> intentionally hiding or giving misleading information'+
                                ' <br/><b>Unknown:</b> I don&apos;t know how the deception was committed.',
                options: ['Abuse of position', 'Failure to declare/disclose','False Representation','Unknown'],
                rules: {
                    required: true
                }
            },         
            {
                questionType: 'select',
                title: 'What was the specific nature of the deception?',
                name: 'deducted-fraud-mandate',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent: '<b>Product substitution/Misselling:</b> using or providing a different product or service than promised or misrepresenting the need for a product or service' +
                ' <br/><b>Falsified invoice:</b> giving misleading information on an invoice (for example, inflating costs)' +
                ' <br/><b>Falsified application:</b> using misleading information when applying for HMG Services (for example, a passport or student loan)' +
                ' <br/><b>Asset misappropriation:</b> misuse of government property (for example, a laptop or phone)' +
                ' <br/><b>Charge evasion:</b> giving misleading information to evade paying money (for example, fees, taxes or fines)' +
                ' <br/><b>Mandate fraud:</b> changing a payment mandate (for example, details for a standing order)' +
                ' <br/><b>Abuse of working hours:</b> falsely claiming time worked (for example, flexitime or annual leave)'+
                ' <br/><b>Travel, Expenses and Subsistence:</b> falsely claiming on government expenses (for example, travel or food)' +
                ' <br/><b>Application for employment:</b> using misleading information to get a role (for example, employment history or qualifications)' +
                ' <br/><b>Misuse of funding::</b> using funds outside the conditions of the agreement',
                options: ['Product substitution/Misselling','Falsified invoice','Falsified application','Asset misappropriation','Charge evasion','Mandate fraud','Abuse of working hours','Travel, Expenses and Subsistence','Application for employment','Misuse of funding'],
                hintOption: 'Select an option',
                rules: {
                    required: true
                },
                errorMessages: { required: 'This field is mandatory' }
            },           
            {
                questionType: 'instanceInput',
                title: 'How many instances occurred?',
                name: 'detected-instance-input',
                hint: `Total number of instances`,
                rules: {
                    required: true
                }
            },
            {
                questionType: 'costInput',
                title: 'What was the value of the fraud?',
                name: 'recovered-fraud-estimated-total',
                hint: `Total value`,
                rules: {
                    required: false
                }
            },                 
            {
                questionType: 'textarea',
                title: 'Comments',
                name: 'deducted-fraud-comments',
                hint: `Please ensure you do not include any personal or sensitive information`,
                rules: {
                    required: false
                }
            },   
            {
                questionType: 'checkbox',
                title: 'Who supported the fraud',
                detailsHeading: 'What were the fraud enablers?',
                detailsContent: '<b>Social media:</b> using this to present disinformation (for example, falsifying personal details)'+
                                '<br><b>Phishing, Vishing and SMShing:</b> use cyber vulnerabilities using digital communication methods (for example, clicking a malicious link)'+
                                '<br><b>Bribery:</b> something given or promised to influence behaviour'+
                                '<br><b>Collusion:</b> deceptive agreement or cooperation (for example, due to lies or manipulation)'+
                                '<br><b>Coercion:</b> forced agreement or cooperation (for example, due to physical threats)'+
                                '<br><b>False identity:</b> using false documents as person or company (for example, a passport or birth certificate)'+
                                '<br><b>Conflict of interest:</b> not sharing relationships which resulted in personal gain'+
                                '<br><b>AI (artificial intelligence):</b> using artificial intelligence to create or present false information',
                name: 'detected-fraud-support',
                options: ['Social media', 'Phishing, SMShing & Vishing', 'Bribery', 'Collusion','Coercion','False Identity','Conflict of Interest','AI (artificial intelligence)'],
                rules: { required: true }
            },   
            {
                questionType: 'radio',
                title: 'Does this relate to BAU or C19 activity?',
                // hint: `You can tick both if applicable`,
                name: 'deducted-fraud-bau-c19',
                options: ['Business as usual (BAU)', 'Covid-19 (C19)'],
                rules: { required: true }
            }, 
            {
                questionType: 'radio',
                title: 'Has this been recovered?',
                name: 'deducted-fraud-recovered',
                options: ['Yes', 'No']
            }
        ]
    },
]
export default addDetectedFraud
